import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { z } from "zod";
import { createUser } from "@/services/userManagementApi";
import { isAuthenticated } from "@/lib/auth";

const registerSchema = z.object({
  email: z.string().trim().email("Invalid email format").max(255, "Email must be less than 255 characters"),
  password: z.string().min(8, "Password must be at least 8 characters").max(128, "Password must be less than 128 characters"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/getting-started", { replace: true });
    }
  }, [navigate]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(null);

    try {
      const validationResult = registerSchema.safeParse({ email, password, confirmPassword });
      if (!validationResult.success) {
        const errorMessage = validationResult.error.errors[0].message;
        setAuthError(errorMessage);
        toast.error(errorMessage);
        setIsLoading(false);
        return;
      }

      await createUser({
        email: validationResult.data.email,
        username: validationResult.data.email,
      } as any);

      toast.success("Registration successful! Please check your email to confirm your account.");
      navigate("/login");
    } catch (error: any) {
      const msg = error?.message || "An unexpected error occurred";
      setAuthError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-7xl px-12 py-8 flex flex-col md:flex-row items-center justify-center gap-20">
        <div className="w-full md:w-1/2 flex justify-center">
          <img src="/lovable-uploads/496f0ef7-a54a-4a74-a39b-0a3c3067bb55.png" alt="Smplify Logo" className="max-w-[250px] md:max-w-[300px] w-full object-contain" />
        </div>
        <div className="w-full md:w-1/2 flex justify-center">
          <div className="w-full max-w-md space-y-6 bg-card rounded-lg shadow-lg border p-8">
            {authError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">Create an account</h2>
              <p className="mt-2 text-sm text-muted-foreground">Sign up to get started</p>
            </div>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full" />
              </div>
              <div className="space-y-2">
                <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full" />
              </div>
              <div className="space-y-2">
                <Input type="password" placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="w-full" />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Creating account..." : (<><UserPlus className="w-4 h-4 mr-2" />Sign up</>)}
              </Button>
            </form>
            <div className="text-center text-sm text-muted-foreground">
              <p>Already have an account?{" "}<Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
